import React, { useMemo } from 'react'

import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import find from 'lodash/find'
import uniqBy from 'lodash/uniqBy'

import { BoxContainer, ChartTitle, Column } from '../Layout'
import { chartAxisProps, chartGridProps } from '../Charts'
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Area } from 'recharts'
import { findLastTimeCoordinateInPast } from '../../../dashboardUtils'

import { colorArray } from '../colorArray'
import themeColors from '@layers-frontend/commons/styles/themeColors'
import styled from 'styled-components'
import keys from 'lodash/keys'
import sortBy from 'lodash/sortBy'
import unionBy from 'lodash/unionBy'
import moment from 'moment/moment'
import createSeasonEvolutionTooltip from './createSeasonEvolutionTooltip'
import isNil from 'lodash/isNil'

const TitleContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`

const EstimationChart = ({ t, title, data, chartsWidth, yLabel }) => {
  const { normalized } = data

  const selectedFieldsIds = Object.keys(normalized)
  const dateFormatter = date => moment(date).format('DD/MM/YYYY')

  const datesArray = useMemo(() => {
    let dates = []
    selectedFieldsIds.forEach(fieldId => {
      if (isEmpty(normalized)) return
      if (isEmpty(normalized[fieldId])) return

      const fieldDates = map(keys(normalized[fieldId].byDate), date => ({
        date: Number(date)
      }))
      dates = sortBy(unionBy(dates, fieldDates, 'date'), ['date'])
    })
    return dates
  }, [normalized, selectedFieldsIds])

  const sortedByDateByField = useMemo(() => {
    return selectedFieldsIds.reduce((acc, fieldId) => {
      const byDate = get(normalized, `[${fieldId}].byDate`)

      if (!byDate) return acc

      acc[fieldId] = Object.fromEntries(Object.entries(byDate).sort(([a], [b]) => Number(a) - Number(b)))

      return acc
    }, {})
  }, [normalized, selectedFieldsIds])

  const getAverageCurve = ({ date }) => {
    const avgsByDate = map(selectedFieldsIds, fieldId => get(normalized, `[${fieldId}].byDate[${date}].avg`))
    return find(avgsByDate, value => !isNil(value))
  }

  const getMinCurve = ({ date }) => {
    const minsByDate = map(selectedFieldsIds, fieldId => get(normalized, `[${fieldId}].byDate[${date}].min`))
    return find(minsByDate, value => !isNil(value))
  }

  const getMaxCurve = ({ date }) => {
    const maxsByDate = map(selectedFieldsIds, fieldId => get(normalized, `[${fieldId}].byDate[${date}].max`))
    return find(maxsByDate, value => !isNil(value))
  }

  const getRangeCurve = ({ date }) => {
    const min = getMinCurve({ date })
    const max = getMaxCurve({ date })
    if (isNil(min) || isNil(max)) return
    return max - min
  }

  const getFieldCurve = (fieldId, dataPeriod) => {
    if (!data || data.length < 1) return

    const dataByDate = get(sortedByDateByField, `[${fieldId}]`)
    const lastPastDate = findLastTimeCoordinateInPast(dataByDate)

    return ({ date }) => {
      const currentData = get(data, `normalized[${fieldId}][byDate][${date}]`)

      if (!currentData || currentData < 0) return
      if (dataPeriod === 'past' && currentData.date > lastPastDate) return
      if (dataPeriod === 'future' && currentData.date < lastPastDate) return

      return currentData.estimation
    }
  }

  const getTooltipData = payload => {
    const uniqueItems = uniqBy(payload, 'name')

    return map(uniqueItems, item => {
      const color = get(item, 'color')

      if (item.name === 'max') {
        const date = get(payload, '[0].payload.date')

        const maxValue = getMaxCurve({ date })
        if (maxValue == null) return

        return { label: t('max'), value: maxValue.toFixed(2), color }
      }

      const value = get(item, 'value')
      if (value == null) return

      if (item.name === 'avg') {
        return { label: t('average'), value: value.toFixed(2), color }
      }

      if (item.name === 'min') {
        return { label: t('min'), value: value.toFixed(2), color }
      }

      const currentField = normalized[item.name].byDate
      const label = Object.values(currentField)[0].field_name
      return { label, value: value.toFixed(2), color }
    })
  }

  return (
    !isEmpty(datesArray) && (
      <Column marginBottom="30px" width={chartsWidth} transition="0.3s ease-in-out">
        <BoxContainer>
          <TitleContainer>
            <ChartTitle>{t(title)}</ChartTitle>
          </TitleContainer>
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart fill={themeColors.lightBlue} height={4500} data={datesArray} margin={{ top: 20, bottom: 20, left: 0, right: 20 }}>
              <XAxis
                {...chartAxisProps}
                dataKey={'date'}
                label={{ value: t('estimation execution date'), position: 'insideBottom', offset: -15 }}
                scale="time"
                type="number"
                domain={[datesArray[0].date, datesArray[datesArray.length - 1].date]}
                tickFormatter={dateFormatter}
              />
              <YAxis {...chartAxisProps} type="number" interval={0} label={{ value: yLabel, angle: -90, position: 'insideLeft', offset: 10 }} />

              <CartesianGrid {...chartGridProps} />

              {createSeasonEvolutionTooltip({
                getTitle: payload => {
                  return dateFormatter(get(payload[0], 'payload.date'))
                },
                getData: getTooltipData
              })}

              {map(selectedFieldsIds, (fieldId, index) => (
                <Line
                  key={`${fieldId}Future`}
                  name={fieldId}
                  dataKey={getFieldCurve(fieldId, 'future')}
                  type="monotone"
                  connectNulls
                  stroke={colorArray[index]}
                  strokeDasharray="5"
                  strokeWidth={2}
                  isAnimationActive={false}
                  dot={false}
                />
              ))}

              {map(selectedFieldsIds, (fieldId, index) => (
                <Line
                  key={`${fieldId}Past`}
                  name={fieldId}
                  dataKey={getFieldCurve(fieldId, 'past')}
                  type="monotone"
                  connectNulls
                  stroke={colorArray[index]}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
              ))}

              <Line
                key="avg"
                name="avg"
                dataKey={getAverageCurve}
                type="monotone"
                connectNulls
                stroke={themeColors.white}
                strokeDasharray="5"
                strokeWidth={2}
                isAnimationActive={false}
                dot={false}
              />

              <Area name="min" dataKey={getMinCurve} fill="transparent" stroke="transparent" stackId="minmax" isAnimationActive={false} />
              <Area
                name="max"
                dataKey={getRangeCurve}
                fill="rgba(255, 255, 255, 0.25)"
                stroke="transparent"
                isAnimationActive={false}
                unit={yLabel}
                stackId="minmax"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </BoxContainer>
      </Column>
    )
  )
}

export default EstimationChart
